import { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ArtistCard from './artist-card';
import { ShowWithDetails, ArtistWithShows } from '@shared/schema';

interface FeaturedShowsCarouselProps {
  shows: ShowWithDetails[];
  onViewDetails: (show: ShowWithDetails) => void;
}

export default function FeaturedShowsCarousel({ shows, onViewDetails }: FeaturedShowsCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showsPerView, setShowsPerView] = useState(4);
  const containerRef = useRef<HTMLDivElement>(null);

  // Extract unique artists from shows while maintaining show priority order
  const artists = useMemo(() => {
    const seenArtistIds = new Set<number>();
    const uniqueArtists: ArtistWithShows[] = [];

    for (const show of shows) {
      if (!seenArtistIds.has(show.artist.id)) {
        seenArtistIds.add(show.artist.id);
        // Get shows for this artist from the featured list
        const artistShows = shows.filter(s => s.artist.id === show.artist.id);
        uniqueArtists.push({
          ...show.artist,
          upcomingShows: artistShows,
          shows: artistShows, // Alias for filter-engine compatibility
          // Backend adds totalShows to the artist object (count of all upcoming shows)
          totalShows: (show.artist as any).totalShows || artistShows.length,
          nextShowDate: artistShows.length > 0 ? (artistShows[0].date as any as string).slice(0, 10) : null
        });
      }
    }

    return uniqueArtists;
  }, [shows]);

  // Responsive shows per view
  useEffect(() => {
    const updateShowsPerView = () => {
      if (window.innerWidth >= 1280) {
        setShowsPerView(4); // xl screens
      } else if (window.innerWidth >= 1024) {
        setShowsPerView(3); // lg screens
      } else if (window.innerWidth >= 768) {
        setShowsPerView(2); // md screens
      } else {
        setShowsPerView(1); // sm screens
      }
    };

    updateShowsPerView();
    window.addEventListener('resize', updateShowsPerView);
    return () => window.removeEventListener('resize', updateShowsPerView);
  }, []);

  // Create infinite scroll by duplicating artists array multiple times
  const infiniteArtists = [...artists, ...artists, ...artists];
  const startIndex = artists.length; // Start at the middle section

  useEffect(() => {
    setCurrentIndex(startIndex);
  }, [startIndex]);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev + 1);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentIndex(prev => prev - 1);
  };

  const handleTransitionEnd = () => {
    setIsTransitioning(false);

    // Reset position when reaching boundaries for seamless infinite scroll
    if (currentIndex >= artists.length * 2) {
      setCurrentIndex(artists.length);
    } else if (currentIndex < artists.length) {
      setCurrentIndex(artists.length * 2 - 1);
    }
  };

  const getActiveIndex = () => {
    // Get the actual index in the original artists array for dot indicator
    return currentIndex % artists.length;
  };

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement)) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          prevSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextSlide();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      {/* Carousel Navigation */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex space-x-2" role="tablist" aria-label="Carousel navigation">
          {artists.map((_, index) => {
            const activeIndex = getActiveIndex();
            const isActive = index === activeIndex;
            return (
              <button
                key={index}
                role="tab"
                aria-selected={isActive}
                aria-label={`Go to artist ${index + 1} of ${artists.length}`}
                className={`w-2 h-2 rounded-full transition-all duration-300 hover:scale-150 focus:ring-4 focus:ring-max-brand focus:ring-offset-2 focus:outline-none ${
                  isActive ? 'bg-max-brand w-6 shadow-md' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                onClick={() => setCurrentIndex(artists.length + index)}
              />
            );
          })}
        </div>
        
        <div className="flex space-x-2">
          <Button
            variant="outline"
            aria-label="Previous artists"
            size="icon"
            onClick={prevSlide}
            className="h-10 w-10 hover:bg-max-brand hover:text-white transition-all duration-300 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={nextSlide}
            aria-label="Next artists"
            className="h-10 w-10 hover:bg-max-brand hover:text-white transition-all duration-300 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      {/* Carousel Content */}
      <div className="overflow-hidden">
        <div
          className={`flex ${isTransitioning ? 'transition-transform duration-300 ease-in-out' : ''}`}
          style={{ transform: `translateX(-${currentIndex * (100 / showsPerView)}%)` }}
          onTransitionEnd={handleTransitionEnd}
        >
          {infiniteArtists.map((artist, index) => (
            <div
              key={`${artist.id}-${index}`}
              className="flex-shrink-0 px-3"
              style={{ width: `${100 / showsPerView}%` }}
            >
              <ArtistCard
                artist={artist}
                onViewArtist={() => {
                  // Navigate to artist page preserving any URL filters
                  const params = new URLSearchParams(window.location.search);
                  const queryString = params.toString();
                  window.location.href = `/artist/${artist.id}${queryString ? `?${queryString}` : ''}`;
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Artist Counter */}
      <div className="text-center mt-6 text-sm text-gray-500">
        Scrolling through {artists.length} top-rated artists
      </div>
    </div>
  );
}